/**
 * Hook para gerenciamento de Status/Stories do WhatsApp
 * 
 * FUNCIONALIDADES:
 * - Listar status de uma inbox
 * - Criar e publicar status (texto, imagem, vídeo)
 * - Agendar status para publicação futura
 * - Deletar status
 * - Verificar suporte do provider
 * 
 * @version 1.0.0
 * @release 2025-02-08
 */

import { useCallback, useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { createLogger } from '@/lib/logger';
import { toast } from 'sonner';

// Logger para este hook
const logger = createLogger('useWhatsAppStatus');

// Tipos de status suportados
export type StatusType = 'text' | 'image' | 'video';

// Status do ciclo de vida
export type StatusState = 'pending' | 'scheduled' | 'publishing' | 'published' | 'failed' | 'expired' | 'deleted';

// Interface para um post de status
export interface StatusPost {
  id: string;
  tenant_id: string;
  inbox_id: string;
  status_type: StatusType;
  content: string | null;
  media_url: string | null;
  media_caption: string | null;
  scheduled_at: string | null;
  published_at: string | null;
  expires_at: string | null;
  status: StatusState;
  external_id: string | null;
  provider_response: Record<string, unknown> | null;
  error_message: string | null;
  created_by: string | null;
  created_at: string;
  updated_at: string;
}

// Parâmetros para criar um status
export interface CreateStatusParams {
  inbox_id: string;
  status_type: StatusType;
  content?: string;
  media_url?: string;
  media_caption?: string;
  scheduled_at?: string;
}

// Providers que suportam status
const SUPPORTED_PROVIDERS = ['z-api', 'waha'];

/**
 * Verifica se um provider suporta publicação de status
 */
export function isStatusSupported(providerSlug: string | undefined): boolean {
  if (!providerSlug) return false;
  return SUPPORTED_PROVIDERS.includes(providerSlug);
}

/**
 * Hook principal para gerenciamento de status do WhatsApp
 */
export function useWhatsAppStatus(inboxId?: string) {
  const queryClient = useQueryClient();
  const [isCreating, setIsCreating] = useState(false);

  // ============================
  // QUERY: Listar status da inbox
  // ============================
  const {
    data: statusPosts,
    isLoading,
    error,
    refetch,
  } = useQuery({
    queryKey: ['whatsapp-status', inboxId],
    queryFn: async (): Promise<StatusPost[]> => {
      if (!inboxId) return [];

      logger.debug('Buscando status da inbox', { inboxId });

      // Busca diretamente da tabela (RLS protege por tenant)
      const { data, error } = await supabase
        .from('whatsapp_status_posts')
        .select('*')
        .eq('inbox_id', inboxId)
        .neq('status', 'deleted')
        .order('created_at', { ascending: false });

      if (error) {
        logger.error('Erro ao buscar status', { error });
        throw error;
      }

      return (data || []) as StatusPost[];
    },
    enabled: !!inboxId,
    staleTime: 30000, // 30 segundos
    refetchInterval: 60000, // Atualiza a cada 1 minuto
  });

  // ============================
  // MUTATION: Criar/publicar status
  // ============================
  const createStatusMutation = useMutation({
    mutationFn: async (params: CreateStatusParams): Promise<StatusPost> => {
      setIsCreating(true);

      // Determina a action baseado no tipo
      let action: string;
      if (params.status_type === 'text') action = 'send_text_status';
      else if (params.status_type === 'image') action = 'send_image_status';
      else action = 'send_video_status';

      logger.info('Criando status', { action, inboxId: params.inbox_id });

      const { data, error } = await supabase.functions.invoke('whatsapp-status', {
        body: {
          action,
          inbox_id: params.inbox_id,
          content: params.content,
          media_url: params.media_url,
          media_caption: params.media_caption,
          scheduled_at: params.scheduled_at,
        },
      });

      if (error) {
        logger.error('Erro ao criar status', { error });
        throw new Error(error.message || 'Erro ao criar status');
      }

      if (!data.success) {
        throw new Error(data.error || 'Erro desconhecido ao criar status');
      }

      return data.data as StatusPost;
    },
    onSuccess: (data, variables) => {
      setIsCreating(false);
      
      // Invalida cache para refetch
      queryClient.invalidateQueries({ queryKey: ['whatsapp-status', variables.inbox_id] });
      queryClient.invalidateQueries({ queryKey: ['whatsapp-status-count'] });

      // Toast de sucesso
      if (variables.scheduled_at) {
        toast.success('Status agendado com sucesso!');
      } else {
        toast.success('Status publicado com sucesso!');
      }
    },
    onError: (error: Error) => {
      setIsCreating(false);
      logger.error('Falha ao criar status', { error: error.message });
      toast.error(`Erro ao criar status: ${error.message}`);
    },
  });

  // ============================
  // MUTATION: Deletar status
  // ============================
  const deleteStatusMutation = useMutation({
    mutationFn: async (statusId: string): Promise<void> => {
      logger.info('Deletando status', { statusId });

      const { data, error } = await supabase.functions.invoke('whatsapp-status', {
        body: {
          action: 'delete_status',
          status_id: statusId,
        },
      });

      if (error) {
        logger.error('Erro ao deletar status', { error });
        throw new Error(error.message || 'Erro ao deletar status');
      }

      if (!data.success) {
        throw new Error(data.error || 'Erro desconhecido ao deletar status');
      }
    },
    onSuccess: () => {
      // Invalida cache para refetch
      queryClient.invalidateQueries({ queryKey: ['whatsapp-status'] });
      queryClient.invalidateQueries({ queryKey: ['whatsapp-status-count'] });
      
      toast.success('Status removido com sucesso!');
    },
    onError: (error: Error) => {
      logger.error('Falha ao deletar status', { error: error.message });
      toast.error(`Erro ao deletar status: ${error.message}`);
    },
  });

  // ============================
  // Função auxiliar: criar status
  // ============================
  const createStatus = useCallback(async (params: CreateStatusParams) => {
    return createStatusMutation.mutateAsync(params);
  }, [createStatusMutation]);

  // ============================
  // Função auxiliar: deletar status
  // ============================
  const deleteStatus = useCallback(async (statusId: string) => {
    return deleteStatusMutation.mutateAsync(statusId);
  }, [deleteStatusMutation]);

  // ============================
  // Contadores para UI
  // ============================
  const activeCount = statusPosts?.filter(
    p => p.status === 'published' || p.status === 'scheduled'
  ).length || 0;

  const publishedCount = statusPosts?.filter(
    p => p.status === 'published'
  ).length || 0;

  const scheduledCount = statusPosts?.filter(
    p => p.status === 'scheduled'
  ).length || 0;

  const failedCount = statusPosts?.filter(
    p => p.status === 'failed'
  ).length || 0;

  return {
    // Dados
    statusPosts: statusPosts || [],
    
    // Estados
    isLoading,
    isCreating: isCreating || createStatusMutation.isPending,
    isDeleting: deleteStatusMutation.isPending,
    error,

    // Contadores
    activeCount,
    publishedCount,
    scheduledCount,
    failedCount,

    // Ações
    createStatus,
    deleteStatus,
    refetch,

    // Helpers
    isStatusSupported,
  };
}

/**
 * Hook para buscar contagem de status ativos por inbox
 * Útil para exibir badges no painel de status
 */
export function useStatusCountByInbox(inboxIds: string[]) {
  return useQuery({
    queryKey: ['whatsapp-status-count', inboxIds],
    queryFn: async (): Promise<Record<string, number>> => {
      if (!inboxIds.length) return {};

      // Busca contagem de status ativos (published ou scheduled) por inbox
      const { data, error } = await supabase
        .from('whatsapp_status_posts')
        .select('inbox_id')
        .in('inbox_id', inboxIds)
        .in('status', ['published', 'scheduled']);

      if (error) {
        logger.error('Erro ao buscar contagem de status', { error });
        throw error;
      }

      // Agrupa por inbox_id
      const counts: Record<string, number> = {};
      for (const row of data || []) {
        counts[row.inbox_id] = (counts[row.inbox_id] || 0) + 1;
      }

      return counts;
    },
    enabled: inboxIds.length > 0,
    staleTime: 30000,
  });
}
